import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Settings, Shield, Menu } from "lucide-react";

interface NavbarSection {
  id: string;
  name: string;
  display_name: string;
  section_type: string;
  parent_section_id?: string;
  route_path?: string;
  sort_order: number;
  description?: string;
}

interface NavbarPermission {
  id: string;
  navbar_section_id: string;
  team: string;
  is_enabled: boolean;
}

interface PermissionRow {
  section: NavbarSection;
  permissions: Record<string, boolean>;
}

const TEAMS = ["Operations", "Advisory", "Investment", "Admin"] as const;

const NavbarPermissionsManager = () => {
  const [sections, setSections] = useState<NavbarSection[]>([]);
  const [permissions, setPermissions] = useState<NavbarPermission[]>([]);
  const [permissionRows, setPermissionRows] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (sections.length > 0) buildPermissionMatrix();
  }, [sections, permissions]);

  const fetchData = async () => {
    try {
      const [sectionsRes, permsRes] = await Promise.all([
        supabase.from("navbar_sections").select("*").order("sort_order"),
        supabase.from("navbar_permissions").select("id, navbar_section_id, team, is_enabled"),
      ]);
      if (sectionsRes.error) throw sectionsRes.error;
      if (permsRes.error) throw permsRes.error;
      setSections(sectionsRes.data || []);
      setPermissions((permsRes.data || []) as NavbarPermission[]);
    } catch (error) {
      console.error("Error fetching navbar data:", error);
      toast({ title: "Error", description: "Failed to load navbar permissions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const buildPermissionMatrix = () => {
    const topLevel = sections.filter((s) => !s.parent_section_id);
    const children = sections.filter((s) => s.parent_section_id);
    const rows: PermissionRow[] = [];

    const buildRow = (section: NavbarSection): PermissionRow => {
      const perms: Record<string, boolean> = {};
      TEAMS.forEach((team) => {
        const p = permissions.find((x) => x.navbar_section_id === section.id && x.team === team);
        perms[team] = p?.is_enabled ?? false;
      });
      return { section, permissions: perms };
    };

    topLevel.forEach((s) => {
      rows.push(buildRow(s));
      children.filter((c) => c.parent_section_id === s.id).forEach((c) => rows.push(buildRow(c)));
    });
    setPermissionRows(rows);
  };

  const updatePermission = async (sectionId: string, team: string, enabled: boolean) => {
    try {
      const existing = permissions.find((p) => p.navbar_section_id === sectionId && p.team === team);
      if (existing) {
        const { error } = await supabase
          .from("navbar_permissions")
          .update({ is_enabled: enabled })
          .eq("id", existing.id);
        if (error) throw error;
        setPermissions((prev) => prev.map((p) => (p.id === existing.id ? { ...p, is_enabled: enabled } : p)));
      } else {
        const { data, error } = await supabase
          .from("navbar_permissions")
          .insert({ navbar_section_id: sectionId, team, role_level: "user", is_enabled: enabled })
          .select("id, navbar_section_id, team, is_enabled")
          .single();
        if (error) throw error;
        if (data) setPermissions((prev) => [...prev, data as NavbarPermission]);
      }
      toast({ title: "Saved", description: "Permission updated" });
    } catch (error) {
      console.error("Error updating permission:", error);
      toast({ title: "Error", description: "Failed to update permission", variant: "destructive" });
    }
  };

  const getTeamBadge = (team: string) => {
    const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      Operations: "outline",
      Advisory: "outline",
      Investment: "secondary",
      Admin: "destructive",
    };
    return <Badge variant={variants[team] ?? "outline"} className="text-xs">{team} Team</Badge>;
  };

  const getSectionIcon = (type: string) =>
    type === "dropdown" ? <Menu className="w-4 h-4 text-primary" /> :
    type === "submenu" ? <Menu className="w-3 h-3 text-muted-foreground ml-2" /> :
    <Shield className="w-4 h-4 text-muted-foreground" />;

  const getSectionName = (s: NavbarSection) => (s.parent_section_id ? `  └─ ${s.display_name}` : s.display_name);

  if (loading) return <div className="flex justify-center items-center h-32">Loading...</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Partner Navbar Access Control
        </CardTitle>
        <CardDescription>
          Toggle which <strong>Teams</strong> (Operations, Advisory, Investment, Admin) see each section of the partner navbar. Super Admins always see everything.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Navbar Section</TableHead>
                  {TEAMS.map((t) => (
                    <TableHead key={t} className="text-center min-w-[120px]">{getTeamBadge(t)}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {permissionRows.map((row) => (
                  <TableRow key={row.section.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getSectionIcon(row.section.section_type)}
                        <span className="font-mono text-sm">{getSectionName(row.section)}</span>
                      </div>
                      {row.section.description && (
                        <div className="text-xs text-muted-foreground mt-1">{row.section.description}</div>
                      )}
                    </TableCell>
                    {TEAMS.map((team) => (
                      <TableCell key={team} className="text-center">
                        <Switch
                          checked={row.permissions[team] || false}
                          onCheckedChange={(checked) => updatePermission(row.section.id, team, checked)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Super Admins bypass these checks and always see every section.</p>
            <p>A user's <em>Team</em> is set on their profile in Team Management.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NavbarPermissionsManager;
